/**
 * 月次経理（FMT）のタスク一覧。
 *
 * 出典はスプレッドシート「経理タスク＆スケジュール v3」の `04_月次経理FMT` タブ
 * （テンプレート。毎月これをコピーして当月分のシートを作っている）を 2026-09-11 に
 * 写したもの。
 *
 * 画面は Firestore に入っている型を読む。ここは「Firestore へ最初に入れる中身」の
 * 定義で、`apps/api/ado-task/scripts/seed-monthly-fmt-items.ts` から流し込む。
 * 画面と投入スクリプトで同じ定義を使いたいので、どちらからも参照できる package に置く。
 *
 * 1 件ずつオブジェクトで書くと 1,700 行を超えて読めなくなるため、シートの行をそのまま
 * `|` 区切りの 1 ブロックで持ち、読み込み時に組み立てる。シートを再度写すときも
 * この塊を差し替えるだけで済む。
 *
 * 取り込んでいない列:
 * - `タスク名`（F）: 大分類 > 中分類 > 小分類（事業）を連結した文字列なので、必要なら画面側で組み立てる
 * - `作業予定日` `作業完了日` `実績時間` `status`（H・I・K・L）: テンプレートでは数式がエラーに
 *   なっており、当月分のシートを作った時点で埋まる値のため
 * - `マニュアル`（O）: 外部サービスのパスワードが平文で書かれているため、リポジトリには持ち込まない
 * - `参考リンク`（N）: 必要になった時点で入れる
 *
 * 担当者は、割り振りを決め直すまでいったん全件 nako にしている（シートの値ではない）。
 *
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business 月次経理（FMT）のタスク一覧。事業・大分類・中分類・予定営業日・想定時間・担当者を持つ。
 * @graph-connects none
 */
/**
 * シートの行。`事業|大分類|中分類|タスク名|予定営業日|想定時間|担当者` の順。
 * @graph-connects none
 */
const ROWS = `
aC|00.前準備||フリーレントの税区分修正・クリーニング費用調整|01営業日|0.5|nako
共通|00.前準備||GMO・NPの明細ダウンロード|毎月25日|0.25|nako
共通|00.前準備||セゾンクレカ・モリモカード・UPSIDER利用明細DL＆SCSK共有|01営業日|0.25|nako
共通|00.前準備||チェリ子の更新|01営業日|0.25|nako
共通|01.預金関係||freee残高と月末預金残高との一致確認|04営業日|0.25|nako
共通|01.預金関係||ネットバンキング入出金明細データ格納|08営業日|0.25|nako
共通|01.預金関係||債権債務消込チェック|02営業日|0.25|nako
共通|01.預金関係||通帳記入|01営業日|0.25|nako
共通|02.現金関連||【aC】月末小口現金残高・実査・freee一致確認|01営業日|0.25|nako
共通|02.現金関連||【aC】小口現金・取引登録|01営業日|0.25|nako
共通|02.現金関連||【オーク】小口現金計上＆確認|03営業日|0.25|nako
共通|02.現金関連||【リアルエコセ】小口現金計上＆確認|03営業日|0.25|nako
共通|02.現金関連||現金実査表の作成確認|01営業日|0.25|nako
サロン|02.現金関連||小口現金の確認|03営業日|0.25|nako
共通|03.freee消込||会計freeeと連携していない預金口座の入出金消込登録|04営業日|0.25|nako
共通|03.freee消込||10日の消込|毎月10日|0.25|nako
共通|03.freee消込||10日の消込|毎月25日|0.25|nako
共通|03.freee消込||25日払いの消込|毎月25日|0.25|nako
共通|03.freee消込||AmazonPay入金消込|03営業日|0.25|nako
共通|03.freee消込||すべて消込が完了しているか確認|05営業日|0.25|nako
共通|03.freee消込||月末の消込|01営業日|0.25|nako
共通|03.freee消込||全ての消し込みを行う|03営業日|0.25|nako
共通|03.freee消込||全ての消し込みを行う|03営業日|0.25|nako
共通|03.freee消込||全ての消し込みを行う|03営業日|0.25|nako
共通|03.支払依頼関連||ADOの支払依頼管理スプシを更新|毎月25日|0.25|nako
アウター|03.支払依頼関連||スポットレンタル→アウターに振替|03営業日|0.25|nako
共通|03.支払依頼関連||支払依頼情報の取引登録漏れ確認（後で変更：中に確認事項を入れる）|05営業日|0.25|nako
共通|03.支払依頼関連||承認および取引登録|05営業日|0.25|nako
mall|03.支払依頼関連||売掛金振伝|04営業日|0.25|nako
アウター|03.支払依頼関連||費用按分|07営業日|0.25|nako
共通|03.支払依頼関連|取引登録の際の注意点|【IDA】外注費になっているか確認|04営業日|0.25|nako
小物販売|03.支払依頼関連|取引登録の際の注意点|【アクセサリー】商品仕入高(原価)となっていることを確認|05営業日|0.25|nako
小物販売|03.支払依頼関連|取引登録の際の注意点|【アクセサリー】商品仕入高(原価)に振替|04営業日|0.25|nako
共通|03.支払依頼関連|取引登録の際の注意点|【おかん】前払費用→福利厚生費に振替|04営業日|0.25|nako
共通|03.支払依頼関連|取引登録の際の注意点|【クラスメソッド】aCからmall・スポットレンタルに振替仕訳切る|04営業日|0.25|nako
共通|03.支払依頼関連|取引登録の際の注意点|【クラスメソッド】mall分は事業・セグメント３はaCに修正|04営業日|0.25|nako
共通|03.支払依頼関連|取引登録の際の注意点|【スタイリスト報酬】外注費（原価）と預り金に分割|04営業日|0.25|nako
共通|03.支払依頼関連|取引登録の際の注意点|【仕入高】レンタル用資産or商品に振替|04営業日|0.25|nako
共通|03.支払依頼関連|取引登録の際の注意点|【鹿島建設】前払費用→支払家賃に振替|04営業日|0.25|nako
共通|03.支払依頼関連|取引登録の際の注意点|タイトル「meta」取引先：facebookの支払依頼は相殺|05営業日|0.25|nako
mall|03.支払依頼関連|取引登録の際の注意点|荷造運賃が消耗品費になっているか確認|05営業日|0.25|nako
aC|03.支払依頼関連|取引登録の際の注意点|【オリックス】利息相当分を支払利息に振替え|05営業日|0.25|nako
共通|04.請求書関連||【Pro】品目「法人向け売上」になっているか確認|04営業日|0.25|nako
共通|04.請求書関連||【スタイリスト講習分】入金時の計上になっているか確認|04営業日|0.25|nako
共通|04.請求書関連||請求書からの仕訳登録|01営業日|0.25|nako
共通|04.請求書関連||請求書取引登録網羅性確認|05営業日|0.25|nako
共通|04.請求書関連||請求書送付漏れチェック|10営業日|0.25|nako
共通|04.請求書関連||入金レポートのチェック|01営業日|0.25|nako
aC|04.請求書関連|売上振替|【オーク】売上→倉庫検品料に振替|04営業日|0.25|nako
aC|04.請求書関連|売上振替|【ル・マ・ニエ】売上→商品修繕費に振替|04営業日|0.25|nako
aC|04.請求書関連|売上振替|【三良】売上→倉庫検品料に振替|04営業日|0.25|nako
aC|04.請求書関連|売上振替|【不入流】売上→倉庫検品料に振替|04営業日|0.25|nako
共通|05.経費関連||承認・取引登録|04営業日|0.25|nako
共通|06.クレジットカード関連|確認事項|UPSIDERカード証憑添付チェック|06営業日|0.25|nako
共通|06.クレジットカード関連|確認事項|MJ新聞代_軽減税率8%になっているか確認|02営業日|0.25|nako
共通|06.クレジットカード関連|確認事項|room_交通費経費の証跡シート作成にてアナウンス|02営業日|0.25|nako
共通|06.クレジットカード関連|確認事項|suica経費の金額を集計→各GMに承認もらう|02営業日|0.25|nako
共通|06.クレジットカード関連|取引登録|UPSIDER_ポイント振替|01営業日|0.25|nako
共通|06.クレジットカード関連|取引登録|UPSIDERカード|02営業日|0.25|nako
共通|06.クレジットカード関連|取引登録|セゾンカード|01営業日|0.25|nako
共通|07.支払関連||UFJに1億資金移動（リマインド設定していいか聞く）|08営業日|0.25|nako
共通|07.支払関連||資金繰り表作成及び資金移動指示|07営業日|0.25|nako
aC|07.支払関連||借上社宅の証憑を差し替える|毎月10日|0.25|nako
共通|07.支払関連||15日支払準備（スタイリスト報酬・経費）|毎月10日|0.25|nako
共通|07.支払関連||25日支払準備（freeeの支払管理レポートからスプシ作成）|毎月10日|0.25|nako
共通|07.支払関連||25日支払準備（データ格納）|毎月10日|0.25|nako
共通|07.支払関連||invoiceに来ている請求書の差し替え（1回目）|05営業日|0.25|nako
共通|07.支払関連||invoiceに来ている請求書の差し替え（2回目）|毎月10日|0.25|nako
共通|07.支払関連||次月支払スケジュール決め・共有|毎月25日|0.25|nako
共通|07.支払関連|支払い管理レポートチェック|支払管理レポートのチェック（15日）|毎月10日|0.25|nako
共通|07.支払関連|支払い管理レポートチェック|支払管理レポートのチェック（25日）|毎月25日|0.25|nako
共通|07.支払関連|支払い管理レポートチェック|支払管理レポートのチェック（月初）|最終営業日|0.25|nako
共通|08.GMO関連||GMO口座_残高キャプチャ(月末の退勤直前or月初の出勤時間)|01営業日|0.25|nako
共通|11.売上関連||チャージバック|01営業日|0.25|nako
aC|11.売上関連||監査法人提出用_売上明細のDL動作確認|毎月20日|0.25|nako
aC|11.売上関連|CS入金|売上計上・消込|02営業日|0.25|nako
mall|11.売上関連|CS入金|売上計上・消込|02営業日|0.25|nako
aC|11.売上関連|CS返金|GMO経由_決済登録|03営業日|0.25|nako
aC|11.売上関連|CS返金|GMO経由_送付済リスト入力|03営業日|0.25|nako
aC|11.売上関連|CS返金|銀行振込経由_全部|01営業日|0.25|nako
aC|11.売上関連|CS返金|私物返金の振込確認|03営業日|0.25|nako
mall|11.売上関連|DB|バッチ回す|03営業日|0.25|nako
mall|11.売上関連|DB|売り上げ集計および計上|03営業日|0.25|nako
aC|11.売上関連|DB|売上計上|05営業日|0.25|nako
aC|11.売上関連|個別計上|【eGift】売上計上|03営業日|0.25|nako
aC|11.売上関連|個別計上|【エコセール・小物】売上計上・消込|03営業日|0.25|nako
aC|11.売上関連|個別計上|【リアルエコセ】売上計上・消込|03営業日|0.25|nako
mall|11.売上関連|個別計上|【リアルエコセ】売上計上・消込|03営業日|0.25|nako
サロン|11.売上関連|個別計上|売上計上・消込|03営業日|0.25|nako
スポットレンタル|11.売上関連|個別計上|売上計上・消込|03営業日|0.25|nako
共通|12.人件費||【立替金 振伝】貸付金振替|04営業日|0.25|nako
共通|12.人件費|15日払給与|スタイリスト原価振替|03営業日|0.25|nako
共通|12.人件費|15日払給与|前月給与消込|毎月15日|0.25|nako
共通|12.人件費|15日払給与|非正規社員雑給計上（15日払い）|毎月10日|0.25|nako
共通|12.人件費|25日払給与|ベトナムメンバー家賃・給与の精算（※立替家賃に注意）|毎月25日|0.25|nako
共通|12.人件費|25日払給与|給与消込|毎月25日|0.25|nako
mall|12.人件費|25日払給与|人件費振替|03営業日|0.25|nako
Port|12.人件費|25日払給与|人件費振替|03営業日|0.25|nako
スポットレンタル|12.人件費|25日払給与|人件費振替|03営業日|0.25|nako
共通|12.人件費|25日払給与|正社員給与_未収入金に振り替え。スプシとの残高確認|毎月25日|0.25|nako
共通|12.人件費|25日払給与|正社員給与計上（非正規25日払いを含む）|毎月25日|0.25|nako
共通|12.人件費|社会保険料|休職者からの入金の共有|01営業日|0.25|nako
共通|12.人件費|社会保険料|雇用保険が増え続けていないか確認|03営業日|0.25|nako
共通|12.人件費|社会保険料|社保料管理台帳を更新|01営業日|0.25|nako
共通|12.人件費|社会保険料|預り金/法定福利費の計上・消込|01営業日|0.25|nako
共通|12.人件費|社会保険料|法定福利費⇒外注費（原価）への振替|04営業日|0.25|nako
共通|12.人件費|社会保険料|未払費用・預り金妥当性確認（社保・雇保分）|毎月25日|0.25|nako
aC|13.レンタル用資産償却費||ステータス確認|01営業日|0.25|nako
aC|13.レンタル用資産償却費||計算・計上|03営業日|0.25|nako
スポットレンタル|13.レンタル用資産償却費||減価償却費・廃棄原価計上|04営業日|0.25|nako
mall|13.レンタル用資産償却費||償却費計算・計上|04営業日|0.25|nako
共通|14.減価償却費||会計freeeの固定資産台帳情報の更新|03営業日|0.25|nako
共通|14.減価償却費||固定資産の内、原価関連項目については減価償却費(原価)へ振替(scmg・log_div両方)（SCMGに固定資産の支払い依頼があるか確認してから着手）|05営業日|0.25|nako
共通|14.減価償却費||固定資産台帳と帳簿残高との整合性確認|04営業日|0.25|nako
共通|14.減価償却費||除却情報に基づく会計freeeの固定資産台帳情報の更新|03営業日|0.25|nako
共通|15.その他 計上||3.株式報酬費用の計上|02営業日|0.25|nako
共通|15.その他 計上||4.資産除去債務の計上|01営業日|0.25|nako
共通|15.その他 計上||フリーレントの金額を考慮し調整仕訳入れる|02営業日|0.25|nako
共通|41.レンタル用資産||お金回りシートと支払依頼の金額の突合|10営業日|0.25|nako
共通|41.レンタル用資産||商品仕入高とDBの整合性確認|毎月25日|0.25|nako
共通|41.レンタル用資産||商品仕入高とDBの整合性確認の記入完了予定日を記載してもらう|05営業日|0.25|nako
mall|42.その他の確認||【GLP】mall保管料は分ける（前払費用⇒翌月費用化）|02営業日|0.25|nako
mall|42.その他の確認||【リマインド（mu to jedi】b下代・支払依頼登録|03営業日|0.25|nako
mall|42.その他の確認||【リマインド（mu to jedi】出口販売分のステータス・販売日変更（オペ依頼）|03営業日|0.25|nako
共通|42.その他の確認||freeeバックアップデータ|毎月25日|0.25|nako
mall|42.その他の確認||mallの売上調整|04営業日|0.25|nako
mall|42.その他の確認||ガルディア回収分の販売原価の確認|02営業日|0.25|nako
mall|42.その他の確認||ガルディア保証金入金の調整|01営業日|0.25|nako
共通|42.その他の確認||スタイリスト単価算出|毎月10日|0.25|nako
共通|42.その他の確認||ベトナムドンの為替レートを確認・記録|01営業日|0.25|nako
共通|42.その他の確認||共通経費の按分|01営業日|0.25|nako
共通|42.その他の確認||月次決算管理シートの確認|04営業日|0.25|nako
共通|42.その他の確認||資本取引に関する月末残高の妥当性の確認（リマイドにリアクションがあるか確認する）|01営業日|0.25|nako
mall|42.その他の確認||事業計画シートメーカー毎物流費・CPOを入力|役員会前|0.25|nako
共通|42.その他の確認||借入債務に関する月末残高の妥当性の確認|04営業日|0.25|nako
mall|42.その他の確認||成長投資分の費用を振替①|04営業日|0.25|nako
mall|42.その他の確認||成長投資分の費用を振替②|04営業日|0.25|nako
mall|42.その他の確認||予実管理|06営業日|0.25|nako
共通|43.PL項目確認||PLの月次推移や部門別・事業別PLのレビューによる異常性確認（PM）|05営業日|0.25|nako
共通|43.PL項目確認||スタイリスト報酬の外注費と雑給が0円になっているかを確認（AM）|05営業日|0.25|nako
共通|43.PL項目確認||外注費・業務委託料分類確認|04営業日|0.25|nako
共通|43.PL項目確認||事業・セグメント３未選択をなくす（1回目）/PL／セグメント3：「その他」は事業：「Port」「スポットレンタル」以外誤りの為、確認（AM）|05営業日|0.25|nako
共通|44.BS項目確認||【Yahoo_前払金】freeeと管理台帳の一致確認|04営業日|0.25|nako
mall|44.BS項目確認||【ガルディア相殺確認】前払費用|04営業日|0.25|nako
共通|44.BS項目確認||BS残高の各科目明細に関する確認(マイナス残や異常な残高の有無確認)（PM)|05営業日|0.25|nako
共通|44.BS項目確認||レンタル用資産に商品が含まれてないか確認|04営業日|0.25|nako
共通|44.BS項目確認||仮払金(原則ゼロとする)|04営業日|0.25|nako
共通|44.BS項目確認||買掛金の取引先未選択をなくす|04営業日|0.25|nako
共通|44.BS項目確認||立替金残高は月末時点で基本的にゼロとする（貸付金は除く）|04営業日|0.25|nako
共通|89.税務関連||源泉税の集計（スタイリスト・業務委託の方）|毎月25日|0.25|nako
共通|89.税務関連||源泉税の納付（給与士業・報酬）|毎月10日|0.25|nako
共通|89.税務関連||消費税チェックリスト|10営業日|0.25|nako
ACE|91.PL共有||PLの作成・共有botが正しく動いているか確認|05営業日|0.25|nako
mall|91.PL共有||PLの作成・共有botが正しく動いているか確認|06営業日|0.25|nako
Port|91.PL共有||PLの作成・共有botが正しく動いているか確認|06営業日|0.25|nako
スポットレンタル|91.PL共有||PLの作成・共有botが正しく動いているか確認|06営業日|0.25|nako
共通|92.取締り役会資料||取締役会資料の作成|08営業日|0.25|nako
共通|93.銀行||BS・PLを各銀行に送付する|毎月17日|0.25|nako
共通|93.銀行||商工中金に資金繰り表を送付する|毎月15日||nako
共通|93.銀行||金融機関別借入残高表を金融機関様宛に作成|毎月25日|0.25|nako
共通|99.四半期||【moreみらい】監査報酬の調整と確認|04営業日|0.25|nako
共通|99.四半期||GMO残高の振替|07営業日|0.25|nako
共通|99.四半期||汚破損の調整|07営業日|0.25|nako
共通|99.四半期||資材費の確認のお知らせ|毎月25日|0.25|nako
共通|99.四半期||資材費の精算|07営業日|0.25|nako
mall|99.四半期||前払金の調整（未着荷分）|03営業日|0.25|nako
共通|99.四半期||相殺仕訳の際の未収収益をやめたい|04営業日|0.25|nako
共通|99.四半期||貸倒損失の調整|07営業日|0.25|nako
mall|99.四半期||売掛金の調整|03営業日|0.25|nako
mall|99.四半期||売上再計算|06営業日|0.25|nako
共通|08.GMO関連||GMOのバックアップデータ取得|毎月20日|0.5|nako
共通|99.四半期||ポイント引当金|09営業日|0.5|nako
mall|04.請求書関連||ヤーマンの請求書を仮払金で決済登録|05営業日|0.25|nako
共通|99.四半期||販管費3期比較|毎月15日|0.5|nako
`;
/**
 * @graph-stack ado-task
 * @graph-domain Finance
 * @graph-business シートの行を月次経理タスクに組み立てる。ID は並び順から作る
 * @graph-connects none
 */
export function parseRows(raw) {
    return raw
        .trim()
        .split('\n')
        .map((line, index) => {
        const [business, majorCategory, middleCategory, title, dueRule, hours, assignee] = line.split('|');
        return {
            id: `fmt-${String(index + 1).padStart(3, '0')}`,
            business: business ?? '',
            majorCategory: majorCategory ?? '',
            // シートの「ー」は区分なしを表すので空にそろえる（写し直しても同じ形になる）
            middleCategory: middleCategory === undefined || middleCategory === 'ー' ? '' : middleCategory,
            title: title ?? '',
            dueRule: dueRule ?? '',
            // シートが空欄の行は「決めていない」であって 0 時間ではないため null にする
            estimatedHours: hours === undefined || hours === '' ? null : Number(hours),
            assignee: assignee ?? '',
        };
    });
}
/** @graph-connects none */
export const MONTHLY_FMT_TASKS = parseRows(ROWS);
//# sourceMappingURL=index.js.map